import React from 'react';
import { Clock, MapPin, FileText, Eye, Check, X, AlertTriangle, DollarSign, User, CheckCircle, Calendar, Receipt } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatTime, formatDate } from '../../../utils/jobGrouping';
import { formatDuration } from '../../../utils/scheduleUtils';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import ContractorActionButtons from './ContractorActionButtons';

/**
 * InstanceRow
 *
 * Bottom-level row for individual job instances.
 * Displays instance details, status, and contractor action buttons.
 * Supports corporate-reports mode with approve/reject/dispute actions.
 */
const InstanceRow = ({
  instance,
  job,
  colorScheme,
  isLast = false,
  instanceNumber,
  onClockIn,
  onClockOut,
  showActions = true,
  showDate = false,
  showEarnings = false,
  timesheet = null,
  isHighlighted = false,
  showInvoice = false,
  onViewInvoice,
  // Payment confirmation props (for contractor earnings view)
  onConfirmPayment,
  isConfirmingPayment = false,
  // View payment proof (for contractor earnings view)
  onViewPaymentProof,
  // Corporate-reports mode props
  showCorporateActions = false,
  isSelected = false,
  onSelect,
  onViewReport,
  onApprove,
  onReject,
  onDispute,
  onResolveDispute,
  onMarkPaid,
  contractor = null
}) => {
  const { preferences } = useUserPreferences();
  const getStatusBadge = () => {
    const status = instance?.status;
    const styles = {
      assigned: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-green-500/20 text-green-400',
      submitted: 'bg-purple-500/20 text-purple-400',
      approved: 'bg-emerald-500/20 text-emerald-400',
      unassigned: 'bg-gray-500/20 text-gray-400'
    };

    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full uppercase',
        styles[status] || 'bg-gray-500/20 text-text-secondary'
      )}>
        {status?.replace('_', ' ') || 'unknown'}
      </span>
    );
  };

  // Pending badges for overdue jobs
  const getPendingBadges = () => {
    const badges = [];

    if (instance?.is_pending) {
      badges.push(
        <span key="pending" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/50">
          <AlertTriangle size={10} />
          Pending
        </span>
      );
    }

    if (instance?.is_pending_report) {
      badges.push(
        <span key="pending-report" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/50">
          <AlertTriangle size={10} />
          Report Overdue
        </span>
      );
    }

    return badges;
  };

  const getTimesheetStatusBadge = () => {
    const status = timesheet?.status;
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400'
    };

    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full uppercase',
        styles[status] || 'bg-gray-500/20 text-text-secondary'
      )}>
        {status || 'pending'}
      </span>
    );
  };

  // Consolidated payment status badge for earnings mode
  // Checks both timesheet and instance status, prefers 'paid' if either has it
  const getPaymentStatusBadge = () => {
    const timesheetStatus = timesheet?.status;
    const instanceStatus = instance?.status;
    const status = (timesheetStatus === 'paid' || instanceStatus === 'paid')
      ? 'paid'
      : (timesheetStatus || instanceStatus || 'pending');

    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      paid: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    const labels = {
      pending: 'Pending',
      approved: 'Approved',
      paid: 'Paid',
      rejected: 'Rejected'
    };

    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full border',
        styles[status] || styles.pending
      )}>
        {labels[status] || status}
      </span>
    );
  };

  // Corporate-reports mode status badge
  const getCorporateStatusBadge = () => {
    const status = instance?.status;
    const statusConfig = {
      submitted: { label: 'Pending Review', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      approved: { label: 'Approved', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
      disputed: { label: 'Disputed', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
      paid: { label: 'Paid', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      needs_revision: { label: 'Needs Revision', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' }
    };
    const config = statusConfig[status] || statusConfig.submitted;
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium border', config.className)}>
        {config.label}
      </span>
    );
  };

  // Corporate-reports mode render
  if (showCorporateActions) {
    const status = instance?.status;
    return (
      <div
        id={`job-card-${instance?.id}`}
        className={cn(
          'pl-10 pr-3 py-3 transition-all duration-300',
          colorScheme?.instance,
          !isLast && 'border-b border-dotted border-border',
          isHighlighted && 'ring-2 ring-orange-500 ring-offset-2 ring-offset-background'
        )}
      >
        <div className="flex items-center gap-3">
          {/* Checkbox for selection */}
          {onSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(e.target.checked)}
              className="w-4 h-4 rounded border-border bg-surface text-primary focus:ring-primary focus:ring-offset-background flex-shrink-0"
            />
          )}

          {/* Contractor Info */}
          <div className="flex items-center gap-2 min-w-[140px]">
            <div className="w-7 h-7 bg-surface rounded-full flex items-center justify-center flex-shrink-0">
              {contractor?.avatar_url ? (
                <img
                  src={contractor.avatar_url}
                  alt={contractor.full_name}
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <User size={14} className="text-text-secondary" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-text-primary text-sm font-medium truncate">{contractor?.full_name || 'Unknown'}</p>
            </div>
          </div>

          {/* Instance number and time */}
          <div className="flex items-center gap-2 text-sm min-w-[100px]">
            <span className="text-text-secondary font-medium">#{instanceNumber}</span>
            <div className="flex items-center text-text-secondary">
              <Clock size={12} className="mr-1 text-text-secondary" />
              <span>
                {formatTime(instance?.scheduled_time, preferences.time_format)}
                {instance?.scheduled_end_time && (
                  <> - {formatTime(instance.scheduled_end_time, preferences.time_format)}</>
                )}
              </span>
              {instance?.scheduled_duration && (
                <span className="ml-1 text-accent text-xs">
                  ({formatDuration(instance.scheduled_duration)})
                </span>
              )}
            </div>
          </div>

          {/* Scheduled Date */}
          <div className="min-w-[90px]">
            <div className="flex items-center gap-1 text-text-secondary text-sm">
              <Calendar size={12} />
              {formatDate(instance?.scheduled_date, preferences.date_format)}
            </div>
          </div>

          {/* Hours Worked */}
          <div className="min-w-[60px]">
            <div className="flex items-center gap-1 text-text-secondary text-sm">
              <Clock size={12} />
              {parseFloat(timesheet?.total_hours || 0).toFixed(1)}h
            </div>
          </div>

          {/* Amount */}
          <div className="min-w-[80px]">
            <div className="flex items-center gap-1 text-green-400 text-sm font-medium">
              <DollarSign size={12} />
              {parseFloat(timesheet?.amount_earned || 0).toFixed(2)}
            </div>
          </div>

          {/* Status Badge */}
          <div className="min-w-[100px] flex items-center gap-2">
            {getCorporateStatusBadge()}
            {/* Overtime Request Indicator */}
            {timesheet?.has_overtime_request && (
              <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full border border-orange-500/30 whitespace-nowrap">
                OT Request
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
            {/* View Report - Always available */}
            {onViewReport && (
              <button
                onClick={onViewReport}
                className="flex items-center gap-1 px-2 py-1 bg-surface hover:bg-surface-light text-text-primary text-xs rounded transition-colors"
                title="View Report"
              >
                <Eye size={12} />
                <span className="hidden lg:inline">View</span>
              </button>
            )}

            {/* View Invoice - Always available */}
            {showInvoice && onViewInvoice && (
              <button
                onClick={onViewInvoice}
                className="flex items-center gap-1 px-2 py-1 bg-surface hover:bg-surface-light text-text-primary text-xs rounded transition-colors"
                title="View Invoice"
              >
                <FileText size={12} />
                <span className="hidden lg:inline">Invoice</span>
              </button>
            )}

            {/* Status-specific actions */}
            {status === 'submitted' && (
              <>
                {onApprove && (
                  <button
                    onClick={onApprove}
                    className="flex items-center gap-1 px-2 py-1 bg-accent hover:bg-accent-600 text-white text-xs rounded transition-colors"
                    title="Approve"
                  >
                    <Check size={12} />
                    <span className="hidden lg:inline">Approve</span>
                  </button>
                )}
                {onReject && (
                  <button
                    onClick={onReject}
                    className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                    title="Reject"
                  >
                    <X size={12} />
                  </button>
                )}
                {onDispute && (
                  <button
                    onClick={onDispute}
                    className="flex items-center gap-1 px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs rounded transition-colors"
                    title="Dispute"
                  >
                    <AlertTriangle size={12} />
                  </button>
                )}
              </>
            )}

            {status === 'approved' && (
              <>
                {onMarkPaid && (
                  <button
                    onClick={onMarkPaid}
                    className="flex items-center gap-1 px-2 py-1 bg-accent hover:bg-accent-600 text-white text-xs rounded transition-colors"
                    title="Mark as Paid"
                  >
                    <DollarSign size={12} />
                    <span className="hidden lg:inline">Mark Paid</span>
                  </button>
                )}
                {onDispute && (
                  <button
                    onClick={onDispute}
                    className="flex items-center gap-1 px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs rounded transition-colors"
                    title="Dispute"
                  >
                    <AlertTriangle size={12} />
                  </button>
                )}
              </>
            )}

            {status === 'disputed' && (
              <>
                {onResolveDispute && (
                  <button
                    onClick={() => onResolveDispute('Resolved')}
                    className="flex items-center gap-1 px-2 py-1 bg-accent hover:bg-accent-600 text-white text-xs rounded transition-colors"
                    title="Resolve Dispute"
                  >
                    <CheckCircle size={12} />
                    <span className="hidden lg:inline">Resolve</span>
                  </button>
                )}
                {onReject && (
                  <button
                    onClick={onReject}
                    className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                    title="Reject"
                  >
                    <X size={12} />
                  </button>
                )}
              </>
            )}

            {status === 'paid' && (
              <div className="flex items-center gap-1 text-purple-400 text-xs">
                <CheckCircle size={12} />
                <span>Completed</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default contractor view render
  return (
    <div
      id={`job-card-${instance?.id}`}
      className={cn(
        'pl-10 pr-3 py-2.5 transition-all duration-300',
        colorScheme?.instance,
        !isLast && 'border-b border-dotted border-border',
        isHighlighted && 'ring-2 ring-orange-500 ring-offset-2 ring-offset-background'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {/* Left side: Instance details */}
        <div className="flex-1 min-w-0">
          {/* Top row: Instance number, time, date, and status */}
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-text-secondary font-medium">#{instanceNumber}</span>
            <div className="flex items-center text-text-secondary">
              <Clock size={12} className="mr-1 text-text-secondary" />
              <span>
                {formatTime(instance?.scheduled_time, preferences.time_format)}
                {instance?.scheduled_end_time && (
                  <> - {formatTime(instance.scheduled_end_time, preferences.time_format)}</>
                )}
              </span>
              {instance?.scheduled_duration && (
                <span className="ml-1 text-accent text-xs">
                  ({formatDuration(instance.scheduled_duration)})
                </span>
              )}
            </div>
            {showDate && (
              <span className="text-text-secondary">
                {formatDate(instance?.scheduled_date, preferences.date_format)}
              </span>
            )}
            {/* In earnings mode, status is shown in the earnings row below; otherwise show instance status */}
            {!showEarnings && getStatusBadge()}
            {/* Pending badges - show below status in same row */}
            {!showEarnings && getPendingBadges()}
          </div>

          {/* Bottom row: Location */}
          <div className="flex items-center text-text-secondary text-xs mt-1">
            <MapPin size={11} className="mr-1 flex-shrink-0" />
            <span className="truncate">{job?.job_site?.address || job?.location_address || 'No location'}</span>
          </div>

          {/* Earnings info (for earnings page) - with payment status beside amount */}
          {showEarnings && timesheet && (
            <div className="flex items-center gap-3 mt-1.5 text-sm">
              <span className="text-text-secondary">
                {parseFloat(timesheet.total_hours || 0).toFixed(1)}h worked
              </span>
              <span className="text-green-400 font-medium">
                ${parseFloat(timesheet.amount_earned || 0).toFixed(2)} earned
              </span>
              {getPaymentStatusBadge()}
            </div>
          )}
        </div>

        {/* Right side: Action Buttons or Invoice Button */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* View Payment Proof button - only shows for paid items in earnings mode */}
          {showEarnings && timesheet && (() => {
            const timesheetStatus = timesheet?.status;
            const instanceStatus = instance?.status;
            const isPaid = timesheetStatus === 'paid' || instanceStatus === 'paid';

            if (!isPaid || !onViewPaymentProof) return null;

            return (
              <button
                onClick={() => onViewPaymentProof(timesheet.id)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all bg-purple-500/20 hover:bg-purple-500/30 text-purple-400"
                title="View payment proof"
              >
                <Receipt size={12} />
                <span className="hidden sm:inline">Proof</span>
              </button>
            );
          })()}
          {/* Payment confirmation button - only shows for paid items in earnings mode */}
          {showEarnings && timesheet && (() => {
            const timesheetStatus = timesheet?.status;
            const instanceStatus = instance?.status;
            const isPaid = timesheetStatus === 'paid' || instanceStatus === 'paid';
            const isConfirmed = timesheet?.contractor_confirmed_received;

            if (!isPaid || !onConfirmPayment) return null;

            return (
              <button
                onClick={() => onConfirmPayment(timesheet.id, !isConfirmed)}
                disabled={isConfirmingPayment}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all',
                  isConfirmed
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-orange-500 hover:bg-orange-600 text-white',
                  isConfirmingPayment && 'opacity-50 cursor-not-allowed'
                )}
                title={isConfirmed ? 'Click to unmark as received' : 'Confirm you received this payment'}
              >
                <CheckCircle size={12} />
                <span className="hidden sm:inline">{isConfirmed ? 'Received' : 'Confirm'}</span>
              </button>
            );
          })()}
          {showInvoice && onViewInvoice && (
            <button
              onClick={onViewInvoice}
              className="flex items-center gap-1 px-2 py-1 bg-surface hover:bg-surface-light text-text-secondary hover:text-text-primary text-xs rounded transition-colors"
              title="View Invoice"
            >
              <FileText size={12} />
              <span className="hidden sm:inline">Invoice</span>
            </button>
          )}
          {showActions && (
            <ContractorActionButtons
              instance={instance}
              onClockIn={onClockIn}
              onClockOut={onClockOut}
              compact
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InstanceRow;
